import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4slcgh5bv6k73lc3kf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Military-Grade Encryption',
      description: 'Your data is protected with AES-256 encryption, the same standard used by governments and security experts worldwide.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Lightning Fast Speeds',
      description: 'Experience blazing-fast connection speeds with our optimized servers. Stream, browse, and download without any lag.'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Server Network',
      description: 'Access content from anywhere with our extensive network of servers strategically located across the globe.'
    },
    {
      icon: 'ri-eye-off-line',
      title: 'No-Log Policy',
      description: 'We never track, collect, or share your private data. Your online activity remains completely private and anonymous.'
    },
    {
      icon: 'ri-device-line',
      title: 'Multi-Device Support',
      description: 'Protect all your devices with a single account. Compatible with Windows, Mac, iOS, Android, and more.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Customer Support',
      description: 'Our dedicated support team is always available to help you with any questions or technical issues.'
    }
  ];

  const vpnBenefits = [
    {
      icon: 'ri-lock-line',
      title: 'Secure Your Connection',
      description: 'Protect your personal information and browsing activity from hackers, ISPs, and prying eyes with our advanced encryption technology.'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Access Global Content',
      description: 'Bypass geo-restrictions and access your favorite content from anywhere in the world. Enjoy unrestricted internet freedom.'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Anonymous Browsing',
      description: 'Browse the internet anonymously without leaving any digital footprints. Your IP address and location remain hidden.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="https://public.readdy.ai/ai/img_res/d1769cf1-8704-4f49-a4b7-b743eafb232e.png" alt="DEN - SAB d.o.o." className="h-12 w-auto" />
              <span className={`text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>DEN - SAB</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Home</a>
              <a href="#vpn-service" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>VPN Service</a>
              <a href="#features" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Features</a>
              <a href="#contact" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
              <a href="#support" className={`text-sm font-medium transition-colors hover:text-teal-500 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Support</a>
            </div>
            <button className="md:hidden w-10 h-10 flex items-center justify-center">
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/d53356ad405272674bd61d344ea26c0f.jpg" 
            alt="VPN Security Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Secure Your Digital Life
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Experience ultimate online privacy and freedom with our premium VPN service. Browse securely, stream freely, and protect your data from anywhere in the world.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#vpn-service" className="px-8 py-4 bg-teal-500 text-white text-base font-semibold rounded-lg hover:bg-teal-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
              Get Started Now
            </a>
            <a href="#features" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-base font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap cursor-pointer">
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose Our VPN Service</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect your online privacy and enjoy unrestricted internet access with our cutting-edge VPN technology designed for modern users.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {vpnBenefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-xl mb-6">
                  <i className={`${benefit.icon} text-3xl text-teal-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-12 text-center shadow-xl">
            <h3 className="text-3xl font-bold text-white mb-4">Ready to Experience True Online Freedom?</h3>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of users who trust our VPN service to protect their digital privacy and unlock the full potential of the internet.
            </p>
            <a href="#contact" className="inline-block px-8 py-4 bg-white text-teal-600 text-base font-semibold rounded-lg hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Contact Us Today
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Powerful Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our VPN service comes packed with advanced features designed to give you the best online experience while keeping you safe and secure.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-gray-50 rounded-xl p-8 hover:bg-teal-50 transition-all duration-300 cursor-pointer border border-gray-100 hover:border-teal-200"
                onMouseEnter={() => setActiveFeature(index)}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white rounded-lg mb-6 group-hover:bg-teal-500 transition-all shadow-md">
                  <i className={`${feature.icon} text-2xl text-teal-500 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions about our VPN service? We're here to help. Reach out to us and we'll get back to you as soon as possible.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-xl text-teal-500"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Email</h3>
                  <a href="mailto:support@den-sabdoo.com" className="text-gray-600 hover:text-teal-500 transition-colors">support@den-sabdoo.com</a>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-xl text-teal-500"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Phone</h3>
                  <a href="tel:+16062686825" className="text-gray-600 hover:text-teal-500 transition-colors">+1 606-268-6825</a>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-teal-500"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Address</h3>
                  <p className="text-gray-600">
                    Ruban 15<br />
                    52100 Pula<br />
                    Croatia
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <form className="space-y-6" data-readdy-form id="contact-form" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm" 
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm" 
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message"
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm" 
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">Message sent successfully! We'll get back to you soon.</p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">Failed to send message. Please try again.</p>
                  </div>
                )}
                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-3 bg-teal-500 text-white text-base font-semibold rounded-lg hover:bg-teal-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Support Center</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Need help? Our support team is available 24/7 to assist you with any questions or technical issues you may encounter.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-6">
                <i className="ri-question-line text-2xl text-teal-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">FAQ</h3>
              <p className="text-gray-600 mb-6">Find answers to commonly asked questions about our VPN service.</p>
              <button className="px-6 py-2 text-teal-500 font-semibold hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                View FAQ
              </button>
            </div>
            <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-6">
                <i className="ri-book-open-line text-2xl text-teal-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Documentation</h3>
              <p className="text-gray-600 mb-6">Access detailed guides and tutorials for setting up and using our VPN.</p>
              <button className="px-6 py-2 text-teal-500 font-semibold hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                Read Docs
              </button>
            </div>
            <div className="bg-white rounded-xl p-8 text-center shadow-lg border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-6">
                <i className="ri-chat-3-line text-2xl text-teal-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Live Chat</h3>
              <p className="text-gray-600 mb-6">Chat with our support team in real-time for immediate assistance.</p>
              <button className="px-6 py-2 text-teal-500 font-semibold hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                Start Chat
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-100">
            <i className="ri-customer-service-2-line text-5xl text-teal-500 mb-6"></i>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Still Need Help?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Our dedicated support team is standing by to help you with any issues or questions you may have about our VPN service.
            </p>
            <a href="#contact" className="inline-block px-8 py-4 bg-teal-500 text-white text-base font-semibold rounded-lg hover:bg-teal-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer">
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-500 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/d1769cf1-8704-4f49-a4b7-b743eafb232e.png" alt="DEN - SAB d.o.o." className="h-10 w-auto" />
                <span className="text-xl font-bold">DEN - SAB</span>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                Protecting your digital privacy with cutting-edge VPN technology since day one.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">Home</a></li>
                <li><a href="#vpn-service" className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">VPN Service</a></li>
                <li><a href="#features" className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">Features</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#contact" className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">Contact</a></li>
                <li><a href="#support" className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">Support Center</a></li>
                <li><a className="text-white/80 hover:text-white transition-colors text-sm cursor-pointer">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-white/80">Ruban 15, 52100 Pula</li>
                <li className="text-white/80">Croatia</li>
                <li><a href="mailto:support@den-sabdoo.com" className="text-white/80 hover:text-white transition-colors">support@den-sabdoo.com</a></li>
                <li><a href="tel:+16062686825" className="text-white/80 hover:text-white transition-colors">+1 606-268-6825</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-white/70 text-sm mb-4 md:mb-0">© 2025 DEN - SAB d.o.o. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white text-sm transition-colors cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
